import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { ConsentCategory, ConsentConfig, ConsentState } from '../types.js'
import { getConsent, setConsent, isExpired, buildConsentState } from '../consent-store.js'
import { getAllCategories, getRequiredCategories } from '../consent-categories.js'

export interface ConsentContextValue {
  /** Current consent state, or null if not yet given */
  consent: ConsentState | null
  /** Whether the cookie banner should be displayed */
  showBanner: boolean
  /** Check if a specific category is consented */
  hasCategory: (category: ConsentCategory) => boolean
  /** Accept all categories */
  acceptAll: () => void
  /** Accept only selected categories (required categories are always included) */
  acceptSelected: (categories: ConsentCategory[]) => void
  /** Reject all non-required categories */
  rejectAll: () => void
  /** Re-show the banner (e.g. from a "Cookie settings" link) */
  reopenBanner: () => void
}

export const ConsentContext = createContext<ConsentContextValue | null>(null)

interface ConsentProviderProps {
  config: ConsentConfig
  children: ReactNode
}

export function ConsentProvider({ config, children }: ConsentProviderProps) {
  const {
    appId,
    version,
    requiredCategories = getRequiredCategories(),
    expiryDays = 365,
  } = config

  const allCategories = useMemo(() => getAllCategories(), [])

  const [consent, setConsentState] = useState<ConsentState | null>(() => {
    const stored = getConsent(appId)
    if (!stored) return null
    // Re-ask if version changed or consent expired
    if (stored.version !== version) return null
    if (isExpired(stored, expiryDays)) return null
    return stored
  })

  const [bannerForced, setBannerForced] = useState(false)

  const showBanner = bannerForced || consent === null

  const persistConsent = useCallback(
    (state: ConsentState) => {
      setConsent(appId, state)
      setConsentState(state)
      setBannerForced(false)
    },
    [appId],
  )

  const acceptAll = useCallback(() => {
    const state = buildConsentState(allCategories, allCategories, requiredCategories, version)
    persistConsent(state)
  }, [allCategories, requiredCategories, version, persistConsent])

  const acceptSelected = useCallback(
    (categories: ConsentCategory[]) => {
      const state = buildConsentState(categories, allCategories, requiredCategories, version)
      persistConsent(state)
    },
    [allCategories, requiredCategories, version, persistConsent],
  )

  const rejectAll = useCallback(() => {
    // Only required categories remain enabled
    const state = buildConsentState([], allCategories, requiredCategories, version)
    persistConsent(state)
  }, [allCategories, requiredCategories, version, persistConsent])

  const hasCategoryFn = useCallback(
    (category: ConsentCategory): boolean => {
      if (!consent) return requiredCategories.includes(category)
      return consent.categories[category] === true
    },
    [consent, requiredCategories],
  )

  const reopenBanner = useCallback(() => {
    setBannerForced(true)
  }, [])

  // Sync across tabs via storage event
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === `saas_consent_${appId}`) {
        const stored = getConsent(appId)
        if (stored && stored.version === version && !isExpired(stored, expiryDays)) {
          setConsentState(stored)
          setBannerForced(false)
        }
      }
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [appId, version, expiryDays])

  const contextValue = useMemo<ConsentContextValue>(
    () => ({
      consent,
      showBanner,
      hasCategory: hasCategoryFn,
      acceptAll,
      acceptSelected,
      rejectAll,
      reopenBanner,
    }),
    [consent, showBanner, hasCategoryFn, acceptAll, acceptSelected, rejectAll, reopenBanner],
  )

  return <ConsentContext.Provider value={contextValue}>{children}</ConsentContext.Provider>
}
